import { useEffect, useState } from "react";
import type { ForwardedRef } from "react";
import { isFunction } from "lodash";
import { useHotkeys } from "react-hotkeys-hook";
import { formattedMessageSentAt } from "@circle-react/helpers/dateTimeHelpers/formattedMessageSentAt";
import { useChatsV2Api } from "@circle-react/hooks/chatsV2";
import { scrollToMessage } from "../../helpers";
import { useDeleteMessageItemModal } from "../DeleteMessageItemModal";
import { useMessageActions } from "../hooks";

export interface UseMessageItemProps {
  calculateScrollInfo: ({ event }: { event: string }) => void;
  chatRoom: any;
  chatThreadsEnabled?: boolean;
  currentParticipant: any;
  isLastMessage?: boolean;
  isViewOnlyMasquerading: boolean;
  message: any;
  participants: any;
  prevDayRef: ForwardedRef<any>;
  setScrollInfo: (scrollInfo: any) => void;
}

export interface UseMessageItemReturn {
  canUserModifyMessage: boolean;
  day: string;
  handleSubmit: (args: any) => void;
  id: string;
  isDayChange: boolean;
  isEditMessage: boolean;
  setEditMessageId: (args: any) => void;
  shouldShowMessageActions: boolean;
  shouldShowRepliesBlock: boolean;
  toggleDeleteConfirmationModal: () => void;
}

export const useMessageItem = ({
  chatRoom,
  chatThreadsEnabled = false,
  currentParticipant,
  isLastMessage = false,
  message,
  participants,
  prevDayRef,
  setScrollInfo,
  calculateScrollInfo,
  isViewOnlyMasquerading,
}: UseMessageItemProps): UseMessageItemReturn => {
  const { updateRichTextMessage }: any = useChatsV2Api();
  const [editMessageId, setEditMessageId] = useState(null);
  const { id, sent_at, replies_count } = message;
  const { day } = formattedMessageSentAt(sent_at);

  const isDayChange = !isFunction(prevDayRef) && prevDayRef?.current !== day;

  if (prevDayRef && !isFunction(prevDayRef)) {
    prevDayRef.current = day;
  }

  const isEditMessage =
    !isViewOnlyMasquerading && id ? editMessageId === id : false;

  const deleteMessageItemModal = useDeleteMessageItemModal();

  const { canUserModifyMessage } = useMessageActions({
    message,
    chatRoom,
  });

  const shouldShowMessageActions =
    !isEditMessage && (canUserModifyMessage || chatThreadsEnabled);

  const handleEsc = () => setEditMessageId(null);

  useEffect(() => {
    if (isEditMessage && isLastMessage) {
      scrollToMessage(id);
    }
  }, [isEditMessage]);

  useHotkeys(
    "esc",
    handleEsc,
    {
      enabled: isEditMessage,
      enableOnContentEditable: true,
    },
    [isEditMessage],
  );

  const handleScrollInfo = (event: any) => {
    setScrollInfo(
      calculateScrollInfo({
        event,
      }),
    );
  };

  const handleSubmit = ({ body, attachments = [], unfurlUrls }: any) => {
    updateRichTextMessage({
      chatRoomUuid: chatRoom.uuid,
      messageId: id,
      rich_text_body: {
        body,
        attachments,
      },
      unfurl_urls: unfurlUrls,
    });
    handleScrollInfo("messageUpdated");
    handleEsc();
  };

  const toggleDeleteConfirmationModal = () => {
    if (deleteMessageItemModal.visible) {
      void deleteMessageItemModal.hide();
    } else {
      void deleteMessageItemModal.show({
        chatRoom,
        currentParticipant,
        handleSubmit,
        isEditMessage,
        message,
        messageId: id,
        participants,
        onConfirm: () => {
          handleScrollInfo("messageDeleted");
        },
        repliesCount: replies_count,
      });
    }
  };

  const shouldShowRepliesBlock = chatThreadsEnabled && replies_count > 0;

  return {
    canUserModifyMessage,
    day,
    handleSubmit,
    id,
    isDayChange,
    isEditMessage,
    setEditMessageId,
    shouldShowMessageActions,
    shouldShowRepliesBlock,
    toggleDeleteConfirmationModal,
  };
};
